import { useState, useEffect } from 'react';
import { EmailVerificationStep } from './EmailVerificationStep';
import { PasswordInputStep } from './PasswordInputStep';
import { PasswordChangeConfirmation } from './PasswordChangeConfirmation';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  userName: string;
  onSuccess: () => void;
}

type PasswordChangeStep = 'verify_email' | 'input_password' | 'completed';

export function PasswordChangeModal({ 
  isOpen, 
  onClose, 
  userEmail, 
  userName,
  onSuccess 
}: PasswordChangeModalProps) {
  const [currentStep, setCurrentStep] = useState<PasswordChangeStep>('verify_email');
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  // 모달이 열릴 때마다 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('verify_email');
      setIsEmailVerified(false);
    }
  }, [isOpen]);

  // 모달 외부 클릭 시 닫기 방지 (body overflow 제어)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleEmailVerified = () => {
    setIsEmailVerified(true);
    setCurrentStep('input_password');
  };

  const handlePasswordChangeCompleted = () => {
    setCurrentStep('completed');
    onSuccess();
    // 2초 후 모달 닫기
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const handleClose = () => {
    if (currentStep !== 'completed') {
      onClose();
    }
  };

  const handleBackToEmailVerification = () => {
    setCurrentStep('verify_email');
    setIsEmailVerified(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div 
        className="relative bg-white rounded-xl w-full max-w-[400px] lg:max-w-[600px] shadow-xl max-h-[95vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 overflow-y-auto p-6">
          {/* 헤더 */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">
              비밀번호 변경
            </h3>
            {currentStep !== 'completed' && (
              <button
                onClick={handleClose}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* 진행 상태 표시 */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'verify_email' ? 'bg-[#4CD964] text-white' : 
                isEmailVerified ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-500'
              }`}>
                1
              </div>
              <div className={`w-12 h-1 ${isEmailVerified ? 'bg-green-200' : 'bg-gray-200'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'input_password' || currentStep === 'completed' ? 'bg-[#4CD964] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                2
              </div>
            </div>
          </div>

          {/* 단계별 컴포넌트 */}
          {currentStep === 'verify_email' && (
            <EmailVerificationStep
              userEmail={userEmail}
              userName={userName}
              onVerified={handleEmailVerified}
              onCancel={handleClose}
            />
          )}

          {currentStep === 'input_password' && (
            <PasswordInputStep
              onCompleted={handlePasswordChangeCompleted}
              onCancel={handleClose}
              onBack={handleBackToEmailVerification}
            />
          )}

          {currentStep === 'completed' && (
            <PasswordChangeConfirmation />
          )}
        </div>
      </div>
    </div>
  );
}